import React, { useMemo, useState } from "react";
import { runtimeConfig } from "../../config/runtime";
import { C } from "../../core/constants";
import { localizedPath } from "../../core/localePaths";
import { useI18n } from "../../features/i18n/LocaleProvider";
import GlassCard from "../../ui/GlassCard";

function readPageFlash() {
  if (typeof window === "undefined") return null;
  const raw = window.PAGE_FLASH;
  if (!raw || typeof raw !== "object") return null;
  const notice = raw.notice;
  const alert = raw.alert;
  if (!notice && !alert) return null;
  return { notice, alert };
}

function getCsrfToken() {
  if (typeof document === "undefined") return "";
  const m = document.querySelector('meta[name="csrf-token"]');
  return m ? m.getAttribute("content") || "" : "";
}

const fieldStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "12px 14px",
  borderRadius: 10,
  border: `1px solid ${C.glassBorder}`,
  background: "rgba(0,0,0,0.25)",
  color: C.textPrimary,
  fontSize: 15,
  fontFamily: "'DM Sans', system-ui, sans-serif",
};

const labelStyle = { display: "block", marginBottom: 8, fontSize: 13, color: C.textSecondary };

export default function ContactPage({ acc }) {
  const { t, locale } = useI18n();
  const [flash] = useState(readPageFlash);
  const csrf = useMemo(() => getCsrfToken(), []);
  const context = runtimeConfig.authContext;
  const isSignedIn = Boolean(context?.signedIn);
  const signedInEmail = context?.user?.email || "";
  const pagePadTop = isSignedIn ? 24 : 68;
  const pagePad = isSignedIn ? "28px 28px 72px" : "48px 28px 72px";

  return (
    <div style={{ position: "relative", zIndex: 1, paddingTop: pagePadTop }}>
      <div style={{ maxWidth: 640, margin: "0 auto", padding: pagePad }}>
        <GlassCard style={{ padding: "36px 32px" }} glow accent={acc.primary}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", color: acc.primary, textTransform: "uppercase", marginBottom: 10 }}>
            {t("contact_page_kicker")}
          </div>
          <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(32px,4vw,44px)", fontWeight: 400, color: C.textPrimary, marginBottom: 12 }}>
            {t("contact_us_h1")}
          </h1>
          <p style={{ fontSize: 14, color: C.textMuted, marginBottom: 28, lineHeight: 1.7 }}>{t("contact_us_intro")}</p>
          {isSignedIn ? (
            <p style={{ fontSize: 13, color: C.textSecondary, marginBottom: 20 }}>
              {t("contact_signed_in_hint")} {signedInEmail}
            </p>
          ) : null}

          {flash?.notice ? (
            <div
              role="status"
              style={{
                marginBottom: 22,
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid rgba(100, 200, 160, 0.35)",
                background: "rgba(100, 200, 160, 0.12)",
                color: C.textSecondary,
                fontSize: 14,
              }}
            >
              {flash.notice}
            </div>
          ) : null}
          {flash?.alert ? (
            <div
              role="alert"
              style={{
                marginBottom: 22,
                padding: "12px 14px",
                borderRadius: 10,
                border: "1px solid rgba(220, 100, 100, 0.35)",
                background: "rgba(220, 100, 100, 0.12)",
                color: C.textSecondary,
                fontSize: 14,
              }}
            >
              {flash.alert}
            </div>
          ) : null}

          <form action={localizedPath("contact", locale)} method="post" noValidate>
            <input type="hidden" name="authenticity_token" value={csrf} autoComplete="off" />
            {!isSignedIn ? (
              <>
                <label htmlFor="contact_submission_name" style={labelStyle}>
                  {t("contact_name_label")}
                </label>
                <input
                  id="contact_submission_name"
                  name="contact_submission[name]"
                  type="text"
                  required
                  maxLength={200}
                  placeholder={t("contact_name_placeholder")}
                  style={{ ...fieldStyle, marginBottom: 20 }}
                  autoComplete="name"
                />
                <label htmlFor="contact_submission_email" style={labelStyle}>
                  {t("contact_email_label")}
                </label>
                <input
                  id="contact_submission_email"
                  name="contact_submission[email]"
                  type="email"
                  required
                  maxLength={254}
                  placeholder={t("contact_email_placeholder")}
                  style={{ ...fieldStyle, marginBottom: 20 }}
                  autoComplete="email"
                />
              </>
            ) : null}
            <label htmlFor="contact_submission_issue_description" style={labelStyle}>
              {t("contact_issue_label")}
            </label>
            <textarea
              id="contact_submission_issue_description"
              name="contact_submission[issue_description]"
              required
              rows={6}
              maxLength={10000}
              placeholder={t("contact_issue_placeholder")}
              style={{ ...fieldStyle, minHeight: 140, resize: "vertical" }}
            />
            <button
              type="submit"
              style={{
                marginTop: 24,
                padding: "12px 28px",
                borderRadius: 999,
                border: `1px solid rgba(127, 220, 200, 0.45)`,
                background: `linear-gradient(135deg, rgba(127,220,200,0.2), rgba(127,180,255,0.12))`,
                color: C.textPrimary,
                fontWeight: 600,
                fontSize: 15,
                cursor: "pointer",
                fontFamily: "'DM Sans', system-ui, sans-serif",
              }}
            >
              {t("contact_submit")}
            </button>
          </form>
        </GlassCard>
      </div>
    </div>
  );
}
